import React, { useMemo } from 'react'
import { FieldProps, getIn } from 'formik'
import { Box, Chip, FormControl, FormLabel } from '@mui/material'
import { isFormLoading, isViewOnly } from './helpers'

type Option = { label: string; value: string | number }

type Props = {
  options: Array<Option>
  multiple?: boolean
  disabled?: boolean
  fullWidth?: boolean
  helperText?: string
  className?: string
  label?: string
}

const CustomChipSelect: React.FunctionComponent<Props & FieldProps> = (props) => {
  const {
    options,
    multiple = false,
    disabled = false,
    fullWidth = false,
    className,
    label,
    field: { name, value },
    form: { setFieldValue, errors, submitCount },
  } = props

  const viewOnly = Boolean(isViewOnly(props))
  const isDisabled = isFormLoading(props) || viewOnly || disabled

  const selectedValues: Array<string | number> = useMemo(() => {
    if (multiple) return Array.isArray(value) ? value : []
    return typeof value === 'string' || typeof value === 'number' ? [value] : []
  }, [multiple, value])

  const toggleValue = (val: string | number) => {
    if (isDisabled) return
    if (multiple) {
      const next = selectedValues.includes(val) ? selectedValues.filter((v) => v !== val) : [...selectedValues, val]
      setFieldValue(name, next)
    } else {
      setFieldValue(name, val)
    }
  }

  const fieldError = submitCount > 0 ? getIn(errors, name) : undefined
  const showError = Boolean(fieldError)

  return (
    <FormControl disabled={isDisabled} fullWidth={fullWidth} className={className}>
      <Box display="flex" flexWrap="wrap">
        {label && <FormLabel sx={{ mr: 1, color: 'text.primary' }}>{label}</FormLabel>}

        {options.map(({ label: optionLabel, value: val }) => {
          const selected = selectedValues.includes(val)
          return (
            <Box key={String(val)} mr={1} mb={1}>
              <Chip
                clickable
                color={getColor(selected, viewOnly)}
                label={optionLabel}
                variant={selected ? undefined : 'outlined'}
                onClick={() => toggleValue(val)}
                aria-pressed={selected}
                role="button"
                sx={{
                  ...(showError && {
                    borderColor: 'error.main',
                  }),
                }}
              />
            </Box>
          )
        })}
      </Box>
    </FormControl>
  )
}

const getColor = (selected: boolean, viewOnly: boolean) => {
  if (selected) {
    return viewOnly ? 'default' : 'primary'
  }
  return undefined
}

export default CustomChipSelect
